import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSearch, HiX } from 'react-icons/hi';

import { materialApi } from '@/api/endpoints/materials';
import { Material, MaterialStatus, MaterialType } from '@/types/material';
import { toast } from '@/store/toastStore';

interface MaterialSearchInputProps {
  value?: Material | null;
  onChange: (material: Material | null) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
  supplierId?: string; // Add supplier_id filter parameter
  enforceAvailability?: boolean; // If true, prevent selection of materials with availableQuantity < 1
}

const MaterialSearchInput: React.FC<MaterialSearchInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  className = '',
  supplierId,
  enforceAvailability = false, // Default to false for backward compatibility
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Material[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Initialize search query with selected material name
  useEffect(() => {
    if (value) {
      setSearchQuery(value.name);
    } else {
      setSearchQuery('');
    }
  }, [value]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim() && searchQuery.trim().length >= 1) {
        searchMaterials(searchQuery.trim());
      } else {
        setSearchResults([]);
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, supplierId]);

  // Search materials - only raw materials for cutting operations with available quantity > 0
  const searchMaterials = async (query: string) => {
    setIsLoading(true);
    try {
      const filters = {
        search: query || undefined,
        status: ['active' as MaterialStatus], // Only active materials
        type: ['raw' as MaterialType], // Only raw materials, exclude semi-finished products
      };
      const response = await materialApi.getMaterials(1, 20, filters);

      setSearchResults(response.data);
      setIsOpen(response.data.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Error searching materials:', error);
      setSearchResults([]);
      setIsOpen(false);
    } finally {
      setIsLoading(false);  
    }
  };



  const handleFocus = () => {
    if (searchResults.length === 0 && !value) {
      searchMaterials('');
    } else {
      setIsOpen(true);
    }
  };

  // Handle hover to pre-load materials (following user preferences)
  const handleInputHover = () => {
    if (searchResults.length === 0 && !value && !isLoading) {
      searchMaterials(''); // Trigger API call with empty search parameter
    }
  };


  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // Clear selection if user is typing
    if (value && newValue !== value.name) {
      onChange(null);
    }
  };

  // Handle material selection
  const handleMaterialSelect = (material: Material) => {
    // Check if material is available (quantity >= 1) only if enforceAvailability is true
    if (enforceAvailability && material.availableQuantity < 1) {
      // Show user feedback for unavailable material using toast notification
      toast.warning(
        t('materials.status.unavailable'),
        t('materials.messages.materialNotAvailable', {
          name: material.name,
          available: material.availableQuantity
        })
      );
      return; // Prevent selection
    }

    // If no current value (used for adding new materials), clear the search
    if (!value) {
      setSearchQuery('');
    } else {
      setSearchQuery(material.name);
    }
    onChange(material);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  // Handle clear selection
  const handleClear = () => {
    setSearchQuery('');
    onChange(null);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };



  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          handleMaterialSelect(searchResults[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside and cleanup hover timeout
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onMouseEnter={handleInputHover}
          placeholder={placeholder || t('materials.search.placeholder')}
          disabled={disabled}
          className={`input-field pr-20 ${error ? 'border-red-500' : ''}`}
        />

        {/* Search icon */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 space-x-1">
          {isLoading && (
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
          )}

          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              title={t('common.clear')}
            >
              <HiX className="h-4 w-4" />
            </button>
          )}

          <HiSearch className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && searchResults.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {searchResults.map((material, index) => {
            const isUnavailable = enforceAvailability && material.availableQuantity < 1;
            return (
              <div
                key={material.id}
                onClick={() => handleMaterialSelect(material)}
                className={`px-4 py-3 border-b border-gray-100 last:border-b-0 ${
                  isUnavailable
                    ? 'cursor-not-allowed bg-gray-100 opacity-60'
                    : 'cursor-pointer hover:bg-gray-50'
                } ${
                  index === selectedIndex && !isUnavailable
                    ? 'bg-blue-50 text-blue-900'
                    : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className={`font-medium ${isUnavailable ? 'text-gray-400' : 'text-gray-900'}`}>
                      {material.name}
                      {isUnavailable && (
                        <span className="ml-2 text-xs text-red-500 font-normal">
                          ({t('materials.status.unavailable')})
                        </span>
                      )}
                    </div>
                    <div className={`text-sm ${isUnavailable ? 'text-gray-400' : 'text-gray-500'}`}>
                      {material.code} • {material.categoryName} • {material.unit} • Available: {material.availableQuantity}
                    </div>
                    {material.supplierNames && material.supplierNames.length > 0 && (
                      <div className={`text-xs mt-1 ${isUnavailable ? 'text-gray-400' : 'text-gray-400'}`}>
                        {t('materials.fields.supplier')}: {material.supplierNames[0]}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    <div className={`text-sm font-medium ${
                      isUnavailable
                        ? 'text-red-500'
                        : material.availableQuantity <= material.minimumStock
                          ? 'text-yellow-600'
                          : 'text-green-600'
                    }`}>
                      {material.availableQuantity} {material.unit}
                    </div>
                    <div className={`text-xs ${isUnavailable ? 'text-gray-400' : 'text-gray-500'}`}>
                      {isUnavailable ? t('materials.status.unavailable') : t('materials.status.available')}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}
    </div>
  );
};

export default MaterialSearchInput;
